/**
 * 会议摘要解析（S11：七段式纪要 + 旧结构兼容）。
 *
 * 单独成纯函数是因为分支多、易错——JSON 解析失败要回退，新旧两种 JSON
 * 结构又要各自识别，必须能脱离组件被直接验证。
 */

import type { MeetingSummaryPayload, MeetingSummaryPayloadV2 } from "../types/meeting";

/** camelCase 化 snake_case 字段名，后端返回原始字典时使用。 */
function toCamelKey(key: string): string {
  return key.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
}

/** JSON.parse 的结果是否为可按字段读取的普通对象——排除数组、null、原始值。 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * 解析写入 ``Meeting.summary`` 的 JSON 字符串。
 *
 * 三种输入形态：
 * - 七段式（S11+）：JSON 含 `onePageSummary` 字段 → {@link MeetingSummaryPayloadV2}
 * - 旧结构（P2~S10）：JSON 含 `summary`/`keyPoints` 等字段 → {@link MeetingSummaryPayload}
 * - 纯文本（P2 之前的存量行）：JSON 解析失败，或解析成功但不是普通对象（如
 *   `"123"`、`"[1,2]"` 这类合法 JSON 但不可能是纪要的畸形数据）→ 包装成旧结构
 *   以统一渲染入口，不把非对象值当成员字段读取导致渲染层崩溃。
 *
 * 空值返回 null，调用方据此展示「尚未生成」空态。数组字段（`keyPoints` /
 * `topics` 等）本函数不做逐项校验——渲染层按 §MeetingMinutesTab 约定对每个
 * 数组字段做 `?? []` 兜底，双重防线，任一处缺失都不至于让页面崩溃。
 */
export function parseMeetingSummary(
  raw: string | null | undefined,
): MeetingSummaryPayload | MeetingSummaryPayloadV2 | null {
  if (!raw || !raw.trim()) return null;

  const asPlainTextFallback = (): MeetingSummaryPayload => ({
    summary: raw,
    keyPoints: [],
    decisions: [],
    actionItemsBrief: [],
    overallTone: "neutral",
  });

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return asPlainTextFallback();
  }
  if (!isPlainObject(parsed)) return asPlainTextFallback();

  const camel: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(parsed)) camel[toCamelKey(k)] = v;
  return camel as unknown as MeetingSummaryPayload | MeetingSummaryPayloadV2;
}
